import { useCallback, useEffect, useState } from 'react'

import { TimeRange } from 'common-constants/timeRange'
import {
  calcDaysDiff,
  calcTimeDiff,
  isDateOverdue,
  showFullHours,
  showFullTime,
} from 'functions/calcTimeDiff'
import { fixCrossPlatformDateString } from 'functions/dateTime'
import { useInterval } from 'hooks/useInterval'

const DEFAULT_VALUE = { days: undefined, time: undefined, hours: undefined }

interface DateParams {
  hours?: number
  days?: number
  time?: string
}

const notEqual = (newDateParams: DateParams, dateParams: DateParams) =>
  newDateParams.hours !== dateParams.hours ||
  newDateParams.time !== dateParams.time ||
  newDateParams.days !== dateParams.days

export const useGetDaysAndTimeLeft = (
  autoDeleteDate: number | string | null
) => {
  const createDateParams = useCallback<() => DateParams>(() => {
    if (!autoDeleteDate || isDateOverdue(autoDeleteDate)) {
      return DEFAULT_VALUE
    }

    const dateNow = new Date()

    return {
      hours: showFullHours(
        calcTimeDiff(fixCrossPlatformDateString(autoDeleteDate), dateNow)
      ),
      time: showFullTime(
        calcTimeDiff(fixCrossPlatformDateString(autoDeleteDate), dateNow)
      ),
      days: calcDaysDiff(fixCrossPlatformDateString(autoDeleteDate), dateNow),
    }
  }, [autoDeleteDate])

  const [dateParams, setDateParams] = useState(createDateParams())

  const update = useCallback(() => {
    if (!autoDeleteDate) {
      return
    }

    const newDateParams = createDateParams()

    if (notEqual(newDateParams, dateParams)) {
      setDateParams(createDateParams())
    }
  }, [autoDeleteDate, createDateParams, dateParams])

  useEffect(() => {
    update()
  }, [update])

  useInterval(() => {
    update()
  }, TimeRange.second)

  return dateParams
}
